/* eslint-disable max-len*/
import { ReactElement, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import ActionButton from '../../../../components/ActionButton/ActionButton';
import StyledSingleDropdown from '../../../../components/StyledSingleDropdown/StyledSingleDropdown';
import { useConfirmationModalContext } from '../../../../hooks/useConfirmationModalContext';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  initializeSchoolYear,
  selectNonPublicStudentFormViewModel,
  selectNonPublicStudentListItems,
  selectNonPublicStudentLoadingFormViewModel,
  selectNonPublicStudentLoadingListItems,
  selectSchoolYear,
  setAllowEdit,
  setHasSearchMatch,
  setNonPublicStudentDataEntryMode,
  setNpsSchoolYear,
} from '../../../../redux/slices/nonPublicStudentSlice';
import { createNotification } from '../../../../redux/slices/notificationSlice';
import {
  fetchNonPublicStudentListItems,
  fetchNpsSchoolYears,
} from '../../../../redux/thunks/nonPublicStudentThunks';
import { TooltipAnchor } from '../../../../services/app.service';
import {
  abortPromiseOnUnmount,
  isValidCode,
} from '../../../../services/base.service';
import { deleteStudent } from '../../../../services/nonPublicStudent.service';
import { appRoutePaths } from '../../../../services/route.service';
import { canUserAccessRoute } from '../../../../services/security.service';
import { ApiResponseModel } from '../../../../types/ApiResponseModel';
import { DataEntryMode } from '../../../../types/DataEntryMode';
import { NonPublicStudentListItemModel } from '../../../../types/NonPublicStudentListItemModel';
import { FormDropdownSelectOption } from '../../../../types/propTypes/FormDropdownPropTypes';
import { ReactSelectOption } from '../../../../types/ReactSelectOption';
import { isStateNonAdmin } from '../../../../utilities/userUtilities';
import StudentPage from '../StudentPage';
import './NonPublicStudentEntryList.css';

const NonPublicStudentEntryList = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const formView = useAppSelector(selectNonPublicStudentFormViewModel);
  const isListLoading = useAppSelector(selectNonPublicStudentLoadingListItems);
  const isFormViewLoading = useAppSelector(
    selectNonPublicStudentLoadingFormViewModel
  );
  const listItems: NonPublicStudentListItemModel[] = useAppSelector(
    selectNonPublicStudentListItems
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getSchoolYearList = formView?.schoolYearOptions || [];
  const schoolYear = useAppSelector(selectSchoolYear);

  const dropdownValue = useMemo(() => {
    if (!schoolYear || !getSchoolYearList || getSchoolYearList.length === 0) {
      return null;
    }

    const matchingOption = getSchoolYearList.find(
      (option) => option.text === schoolYear
    );

    if (matchingOption) {
      return { value: matchingOption.value, label: matchingOption.text };
    }

    const latestOption = getSchoolYearList[0];
    return latestOption
      ? { value: latestOption.value, label: latestOption.text }
      : null;
  }, [schoolYear, getSchoolYearList]);

  const isMostRecentYearSelected = (): boolean => {
    if (!getSchoolYearList || getSchoolYearList.length === 0) {
      return false;
    }
    return getSchoolYearList[0]?.text === schoolYear;
  };

  const canUserDelete = (): boolean => {
    return isMostRecentYearSelected() && !isStateNonAdmin();
  };

  const canUserEdit = (): boolean => {
    return isMostRecentYearSelected() && !isStateNonAdmin();
  };

  const confirmContext = useConfirmationModalContext();

  useEffect(() => {
    dispatch(setHasSearchMatch(false));
  }, [dispatch]);

  useEffect(() => {
    const promise: unknown = dispatch(fetchNpsSchoolYears());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  useEffect(() => {
    if (getSchoolYearList && getSchoolYearList.length > 0) {
      dispatch(initializeSchoolYear());
    }
  }, [dispatch, getSchoolYearList]);

  useEffect(() => {
    let promise: unknown;
    if (schoolYear) {
      promise = dispatch(fetchNonPublicStudentListItems(schoolYear));
    }
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch, schoolYear]);

  const handleOnChange = async (
    option: FormDropdownSelectOption
  ): Promise<void> => {
    const selectedValue = (option as SingleValue<ReactSelectOption>)?.value;
    const selectedLabel = (option as SingleValue<ReactSelectOption>)?.label;
    if (selectedValue && selectedLabel) {
      dispatch(setNpsSchoolYear(option as ReactSelectOption));
    }
  };

  const handleEdit = (nonPublicStudentId: string): void => {
    dispatch(setAllowEdit(isMostRecentYearSelected()));
    navigate(
      appRoutePaths.NonPublicStudentEntryInput(nonPublicStudentId, schoolYear)
    );
  };

  const handleDelete = async (id: string): Promise<void> => {
    confirmContext.setOptions({
      title: `Delete Student?`,
      content: (
        <div className="confirm--message">
          Are you sure you want to delete this student?
        </div>
      ),
      confirmText: 'Yes',
      cancelText: 'No',
      onOk: async (): Promise<ApiResponseModel<unknown>> => {
        const response = await deleteStudent(id);
        if (isValidCode(response.status)) {
          /* istanbul ignore next */
          dispatch(
            fetchNonPublicStudentListItems(
              formView?.selectedSchoolYearOption?.value
            )
          );
          dispatch(
            createNotification({
              severity: 'success',
              children: 'Non public student deleted',
            })
          );
        }
        return response;
      },
    });
    await confirmContext.showConfirmation();
  };

  const handleNew = (): void => {
    dispatch(setAllowEdit(true));
    dispatch(setNonPublicStudentDataEntryMode(DataEntryMode.NEW));
    navigate(appRoutePaths.NonPublicStudentEntryInput('new', schoolYear));
  };

  const canAddNew =
    canUserAccessRoute(appRoutePaths.NonPublicStudentEntryInput()) &&
    isMostRecentYearSelected() &&
    !isStateNonAdmin();

  return (
    <StudentPage
      pageClass="data-input--student--non-public-student-entry--list"
      isLoading={isFormViewLoading || isListLoading}
      loadingDataId={'non-public-student-entry-list-loader'}
    >
      <div className="content-outer-container">
        <div className="content-inner-container">
          <div
            className={
              isStateNonAdmin()
                ? 'school-year-selection-container school-year-padding'
                : 'school-year-selection-container'
            }
          >
            <StyledSingleDropdown
              fieldName="schoolYear"
              options={getSchoolYearList}
              onChange={handleOnChange}
              value={dropdownValue}
              isSearchable={false}
            />
          </div>
          <div className="subheading-and-button-container">
            <h4 data-testid={'component-heading-text'}>
              Non-Public Student Entry
            </h4>
            {!isStateNonAdmin() && (
              <ActionButton
                onClick={handleNew}
                classes={canAddNew ? 'no-wrap-text new-button' : 'hidden'}
                tooltipText="Add New Non-Public Student Entry"
              >
                <>Add New</>
              </ActionButton>
            )}
          </div>
          <div className="table-container">
            <table cellSpacing={0} className="page-view-table">
              <thead>
                <tr>
                  <th>Last Name</th>
                  <th>First Name</th>
                  <th>MSIS ID</th>
                  <th>District</th>
                  <th>School Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listItems?.length > 0 &&
                  listItems.map((nonPublicStudent) => (
                    <tr
                      key={`${nonPublicStudent.id}`}
                      data-testid={nonPublicStudent.id}
                    >
                      <td>{nonPublicStudent.lastOrSurname}</td>
                      <td>{nonPublicStudent.firstName}</td>
                      <td>{nonPublicStudent.studentIdentifier}</td>
                      <td>{nonPublicStudent.district}</td>
                      <td>{nonPublicStudent.schoolType}</td>
                      <td>
                        <div className="action--column">
                          <button
                            data-testid={`edit-button-${nonPublicStudent.id}`}
                            aria-label="Edit"
                            onClick={() => handleEdit(nonPublicStudent.id)}
                            data-tooltip-id={TooltipAnchor}
                            data-tooltip-content={
                              canUserEdit()
                                ? 'View/Edit Student'
                                : 'View Student'
                            }
                          >
                            <img src={'/images/icon-edit.svg'} />
                          </button>
                          {canUserDelete() && (
                            <button
                              data-testid={`edit-button-${nonPublicStudent.id}`}
                              aria-label="Delete"
                              onClick={() => handleDelete(nonPublicStudent.id)}
                              data-tooltip-id={TooltipAnchor}
                              data-tooltip-content={'Delete Student'}
                            >
                              <img src={'/images/icon-trash copy.svg'} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </StudentPage>
  );
};

export default NonPublicStudentEntryList;
